import { Avatar, Button, Modal, Progress, Spin, Tag } from "antd";
import {
  CrownFilled,
  RocketOutlined,
  UserOutlined,
} from "@ant-design/icons";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { OrganizationApi } from "../../../services";
import { actions as commonActions } from "../../../store/commonRedux/actions";
import styles from "./styles.module.css";

const usageLabels = {
  projects: "پروژه‌ها",
  tasks: "تسک‌ها",
  users: "کاربران",
  storage: "فضای ذخیره‌سازی",
};

const formatValue = (item, value) => {
  const formatted = Number(value || 0).toLocaleString("fa-IR", {
    maximumFractionDigits: 1,
  });
  return item.key === "storage" ? `${formatted} مگابایت` : formatted;
};

/* Shared by proactive locks in the UI and reactive 423 responses from the API.
 * A locked entry explains what is unavailable before a request is made; the
 * interceptor remains the safety net for stale tabs and direct API actions. */
const SubModal = () => {
  const modal = useSelector(({ common }) => common.subModal);
  const organization = useSelector(({ auth }) => auth.organization);
  const userId = useSelector(({ auth }) => auth.user?.id);
  const roles = useSelector(({ auth }) => auth.roles);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isOpen = Boolean(modal);
  const details = typeof modal === "object" && modal ? modal : {};

  const isOwner =
    userId != null && String(userId) === String(organization?.owner_id);
  const canManageOrg =
    isOwner ||
    (roles || [])
      .flatMap((role) => role?.permissions || [])
      .some((permission) => permission?.name === "manage_org");

  const usageQuery = useQuery({
    queryKey: ["[Subscription] Usage", organization?.uuid],
    queryFn: () => OrganizationApi.getSubscriptionUsage(),
    enabled: isOpen && Boolean(organization?.uuid),
    staleTime: 30000,
  });

  const subscription = usageQuery.data?.data?.subscription;
  const usage = subscription?.usage || [];
  const managers = subscription?.managers || [];
  const featureTitle =
    typeof details.title === "string" ? details.title : "این قابلیت";
  const close = () => dispatch(commonActions.setSubModal(false));

  return (
    <Modal
      open={isOpen}
      width={560}
      title={null}
      footer={[
        <Button key="close" onClick={close}>
          {canManageOrg ? "فعلاً نه" : "باشه"}
        </Button>,
        canManageOrg ? (
          <Button
            key="subscribe"
            type="primary"
            icon={<RocketOutlined />}
            onClick={() => {
              navigate("/subscriptions");
              close();
            }}
          >
            فعال‌سازی اشتراک
          </Button>
        ) : null,
      ].filter(Boolean)}
      onCancel={close}
      maskStyle={{ backdropFilter: "blur(10px)" }}
    >
      <div className={styles.hero}>
        <span className={styles.heroIcon}>
          <CrownFilled />
        </span>
        <div>
          <Tag bordered={false} color="gold" className={styles.tag}>
            قابلیت اشتراکی
          </Tag>
          <h3 className={styles.title}>{featureTitle} قفل است</h3>
          <p className={styles.description}>
            {canManageOrg
              ? "با فعال‌کردن اشتراک، این قابلیت و تمام امکانات حرفه‌ای سازمان در دسترس قرار می‌گیرند."
              : "این قابلیت در اشتراک فعلی سازمان در دسترس نیست. برای ارتقا با مدیر سازمان هماهنگ کنید."}
          </p>
        </div>
      </div>

      {canManageOrg && (
        <div className={styles.usageBox}>
          <div className={styles.usageHead}>
            <span>مصرف پلن فعلی</span>
            <span className={styles.planName}>
              {subscription?.plan || "رایگان"}
            </span>
          </div>
          <Spin spinning={usageQuery.isLoading}>
            <div className={styles.usageGrid}>
              {usage.map((item) => {
                const percent = item.limit
                  ? Math.min(100, Math.round((item.used / item.limit) * 100))
                  : 0;
                return (
                  <div className={styles.usageItem} key={item.key}>
                    <div className={styles.usageMeta}>
                      <span>{usageLabels[item.key] || item.key}</span>
                      <span>
                        {formatValue(item, item.used)} از{" "}
                        {item.limit == null
                          ? "نامحدود"
                          : formatValue(item, item.limit)}
                      </span>
                    </div>
                    <Progress
                      percent={percent}
                      showInfo={false}
                      strokeColor="var(--ds-accent)"
                      trailColor="var(--ds-border-subtle)"
                      size="small"
                    />
                  </div>
                );
              })}
              {!usageQuery.isLoading && usage.length === 0 && (
                <div className={styles.usageEmpty}>
                  اطلاعات مصرف در دسترس نیست.
                </div>
              )}
            </div>
          </Spin>
        </div>
      )}

      {managers.length > 0 && (
        <div className={styles.managers}>
          <div className={styles.managersTitle}>مدیریت اشتراک سازمان</div>
          <div className={styles.managerList}>
            {managers.map((manager) => (
              <div className={styles.manager} key={manager.id}>
                <Avatar size={30} icon={<UserOutlined />} />
                <span className={styles.managerName}>{manager.name}</span>
                <Tag
                  bordered={false}
                  color={manager.is_owner ? "gold" : "blue"}
                  className={styles.managerRole}
                >
                  {manager.is_owner ? "مالک" : "مدیر سازمان"}
                </Tag>
              </div>
            ))}
          </div>
        </div>
      )}

    </Modal>
  );
};

export default SubModal;
